import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Space, Spin, message, Descriptions, Tabs, Popconfirm, Switch, Divider, Row, Col, Image } from 'antd';
import { EditOutlined, PlusOutlined, EyeOutlined, DeleteOutlined, InfoCircleOutlined, AppstoreOutlined, DollarOutlined, StarOutlined, GlobalOutlined, KeyOutlined, ToolOutlined, PictureOutlined } from '@ant-design/icons';
import { adminRoomService } from '../../services/adminService';
import { formatLkr } from '../../utils/currency';
import ReservationCalendar from '../reservations/ReservationCalendar';
import OffersManagement from './OffersManagement';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoomTypeModal } from './RoomTypeModal';
import { RoomModal } from './RoomModal';
import './Rooms.css';
import { hasRole } from '../../utils/permissions';

const canManageRoomData = (): boolean => hasRole("admin", "manager");
export interface RoomImage {
  id?: string;
  image_url: string;
  alt_text?: string;
  display_order?: number;
  is_primary?: boolean;
}

export interface RoomType {
  id: string;
  name: string;
  slug?: string;
  description: string;
  base_price: number;
  ac_status_preference?: 'ac' | 'non_ac' | 'both';
  ac_base_price?: number;
  non_ac_base_price?: number;
  max_occupancy: number;
  amenities: string[];
  highlight_points?: string[];
  size_sqm: number;
  bed_type: string;
  view_type: string;
  hero_title?: string;
  hero_subtitle?: string;
  hero_image_url?: string;
  overview_kicker?: string;
  overview_title?: string;
  overview_description?: string;
  overview_image_url?: string;
  detail_kicker?: string;
  detail_title?: string;
  detail_description?: string;
  detail_image_url?: string;
  booking_panel_title?: string;
  is_featured?: boolean;
  extra_bed_price?: number;
  images?: RoomImage[];
  created_at?: string;
  updated_at?: string;
}

export interface Room {
  id: string;
  room_number: string;
  room_type_id: string;
  status: 'available' | 'occupied' | 'maintenance' | 'out_of_order' | 'cleaning';
  floor: number;
  maintenance_notes?: string;
  ac_status?: 'ac' | 'non_ac' | 'both';
  last_cleaned?: string;
  created_at?: string;
  updated_at?: string;
  roomType?: RoomType;
}

const Rooms: React.FC = () => {
  const canManage = canManageRoomData();
  const location = useLocation();
  const navigate = useNavigate();

  const pathToTab = (pathname: string): string => {
    if (pathname === '/rooms/list') return '2';
    if (pathname === '/rooms/availability') return '3';
    if (pathname === '/rooms/offers') return '4';
    return '1';
  };

  const tabToPath = (tab: string): string => {
    if (tab === '2') return '/rooms/list';
    if (tab === '3') return '/rooms/availability';
    if (tab === '4') return '/rooms/offers';
    return '/rooms/types';
  };

  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(pathToTab(location.pathname));
  
  // Room Type modal
  const [roomTypeModalVisible, setRoomTypeModalVisible] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState<RoomType | null>(null);
  const [roomTypeForm] = Form.useForm();

  // Room modal
  const [roomModalVisible, setRoomModalVisible] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [roomForm] = Form.useForm();

  // Drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null);
  const [roomDrawerVisible, setRoomDrawerVisible] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  // Fetch data
  useEffect(() => {
    fetchRoomTypes();
    if (selectedTab !== '1') {
      fetchRooms();
    }
  }, [selectedTab]);

  useEffect(() => {
    setSelectedTab(pathToTab(location.pathname));
  }, [location.pathname]);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    const nextPath = tabToPath(tab);
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      setLoading(true);
      const response = await adminRoomService.getAllRoomTypes();
      const rows = Array.isArray(response.data) ? (response.data as RoomType[]) : [];
      setRoomTypes(rows);
    } catch (error) {
      console.error('Error fetching room types:', error);
      message.error('Failed to fetch room types');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await adminRoomService.getAllRooms();
      const rows = Array.isArray(response.data) ? (response.data as Room[]) : [];
      setRooms(rows);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      message.error('Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  };

  // Room Type handlers
  const handleAddRoomType = () => {
    setEditingRoomType(null);
    roomTypeForm.resetFields();
    setRoomTypeModalVisible(true);
  };

  const handleEditRoomType = async (roomType: RoomType) => {
    try {
      setLoading(true);
      const response = await adminRoomService.getRoomType(roomType.id);
      const fullRoomType = (response.data as RoomType) || roomType;

      setEditingRoomType(fullRoomType);
      roomTypeForm.setFieldsValue({
        name: fullRoomType.name,
        slug: fullRoomType.slug,
        description: fullRoomType.description,
        base_price: fullRoomType.base_price,
        ac_status_preference: fullRoomType.ac_status_preference || 'both',
        ac_base_price: fullRoomType.ac_base_price,
        non_ac_base_price: fullRoomType.non_ac_base_price,
        max_occupancy: fullRoomType.max_occupancy,
        amenities: fullRoomType.amenities?.join(', ') || '',
        highlight_points: fullRoomType.highlight_points?.join(', ') || '',
        size_sqm: fullRoomType.size_sqm,
        bed_type: fullRoomType.bed_type,
        view_type: fullRoomType.view_type,
        hero_title: fullRoomType.hero_title,
        hero_subtitle: fullRoomType.hero_subtitle,
        hero_image_url: fullRoomType.hero_image_url,
        overview_kicker: fullRoomType.overview_kicker,
        overview_title: fullRoomType.overview_title,
        overview_description: fullRoomType.overview_description,
        overview_image_url: fullRoomType.overview_image_url,
        detail_kicker: fullRoomType.detail_kicker,
        detail_title: fullRoomType.detail_title,
        detail_description: fullRoomType.detail_description,
        detail_image_url: fullRoomType.detail_image_url,
        booking_panel_title: fullRoomType.booking_panel_title,
        is_featured: Boolean(fullRoomType.is_featured),
        extra_bed_price: fullRoomType.extra_bed_price || 0,
        images: fullRoomType.images || [],
      });
      setRoomTypeModalVisible(true);
    } catch (error) {
      console.error('Error loading room type details:', error);
      message.error('Failed to load room type details');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomTypeSubmit = async (values: Record<string, unknown>) => {
    try {
      setLoading(true);
      const data: Record<string, unknown> = {
        ...values,
        amenities: typeof values.amenities === 'string' ? values.amenities.split(',').map((a) => a.trim()).filter((a) => a.length > 0) : [],
        highlight_points: typeof values.highlight_points === 'string' ? values.highlight_points.split(',').map((a) => a.trim()).filter((a) => a.length > 0) : [],
      };

      if (data.ac_status_preference === 'both') {
        data.base_price = data.non_ac_base_price;
      } else {
        data.ac_base_price = null;
        data.non_ac_base_price = null;
      }
      
      delete data.images;

      const images = Array.isArray(values.images)
        ? (values.images as RoomImage[]).filter((image) => image?.image_url && String(image.image_url).trim().length > 0)
        : [];

      let roomTypeId = editingRoomType?.id;

      if (editingRoomType) {
        await adminRoomService.updateRoomType(editingRoomType.id, data);
        roomTypeId = editingRoomType.id;
        message.success('Room type updated successfully');
      } else {
        const created = await adminRoomService.createRoomType(data);
        roomTypeId = (created.data as RoomType).id;
        message.success('Room type created successfully');
      }

      if (roomTypeId) {
        await adminRoomService.replaceRoomImages(roomTypeId, images);
      }

      setRoomTypeModalVisible(false);
      fetchRoomTypes();
    } catch (error) {
      console.error('Error saving room type:', error);
      message.error('Failed to save room type');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoomType = async (roomType: RoomType) => {
    try {
      setLoading(true);
      await adminRoomService.deleteRoomType(roomType.id);
      message.success('Room type deleted successfully');
      fetchRoomTypes();
    } catch (error) {
      console.error('Error deleting room type:', error);
      message.error('Failed to delete room type');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomGalleryUpload = async (index: number, file: File) => {
    try {
      setLoading(true);
      const response = await adminRoomService.uploadRoomImage(file);
      const imageUrl = String((response.data as { image_url?: string })?.image_url || '');

      if (!imageUrl) {
        message.error('Upload succeeded but no image URL was returned');
        return;
      }

      roomTypeForm.setFieldValue(['images', index, 'image_url'], imageUrl);
      message.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading room gallery image:', error);
      message.error('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomContentUpload = async (
    fieldName: 'hero_image_url' | 'overview_image_url' | 'detail_image_url',
    file: File,
    mediaType: 'image' | 'media'
  ) => {
    try {
      setLoading(true);
      const response = mediaType === 'image'
        ? await adminRoomService.uploadRoomImage(file)
        : await adminRoomService.uploadRoomMedia(file);

      const uploadedUrl = mediaType === 'image'
        ? String((response.data as { image_url?: string })?.image_url || '')
        : String((response.data as { media_url?: string })?.media_url || '');

      if (!uploadedUrl) {
        message.error('Upload succeeded but no URL was returned');
        return;
      }

      roomTypeForm.setFieldValue(fieldName, uploadedUrl);
      message.success('Media uploaded successfully');
    } catch (error) {
      console.error('Error uploading room content media:', error);
      message.error('Failed to upload media');
    } finally {
      setLoading(false);
    }
  };

  // Room handlers
  const handleAddRoom = () => {
    setEditingRoom(null);
    roomForm.resetFields();
    setRoomModalVisible(true);
  };

  const handleEditRoom = (room: Room) => {
    setEditingRoom(room);
    roomForm.setFieldsValue({
      room_number: room.room_number,
      room_type_id: room.room_type_id,
      floor: room.floor,
      status: room.status,
      ac_status: room.ac_status || 'both',
      maintenance_notes: room.maintenance_notes,
    });
    setRoomModalVisible(true);
  };

  const handleRoomSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (editingRoom) {
        await adminRoomService.updateRoom(editingRoom.id, values);
        message.success('Room updated successfully');
      } else {
        await adminRoomService.createRoom(values);
        message.success('Room created successfully');
      }

      setRoomModalVisible(false);
      fetchRooms();
    } catch (error) {
      console.error('Error saving room:', error);
      message.error('Failed to save room');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoom = async (room: Room) => {
    try {
      setLoading(true);
      await adminRoomService.deleteRoom(room.id);
      message.success('Room deleted successfully');
      fetchRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
      message.error('Failed to delete room');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      available: 'green',
      occupied: 'blue',
      cleaning: 'geekblue',
      maintenance: 'orange',
      out_of_order: 'red',
      reserved: 'purple',
      blocked: 'volcano',
    };
    return colors[status] || 'default';
  };

  const roomTypeColumns = [
    {
      title: 'Room Type Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: (a: RoomType, b: RoomType) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Price/Night',
      dataIndex: 'base_price',
      key: 'base_price',
      width: 110,
      render: (price: number) => formatLkr(price),
      align: 'right' as const,
      sorter: (a: RoomType, b: RoomType) => a.base_price - b.base_price,
    },
    {
      title: 'Extra Bed Price',
      dataIndex: 'extra_bed_price',
      key: 'extra_bed_price',
      width: 130,
      render: (price: number) => price !== undefined ? formatLkr(price) : 'N/A',
      align: 'right' as const,
      sorter: (a: RoomType, b: RoomType) => (a.extra_bed_price || 0) - (b.extra_bed_price || 0),
    },
    {
      title: 'Max Occupancy',
      dataIndex: 'max_occupancy',
      key: 'max_occupancy',
      width: 110,
      align: 'center' as const,
    },
    {
      title: 'Size (sqm)',
      dataIndex: 'size_sqm',
      key: 'size_sqm',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Bed Type',
      dataIndex: 'bed_type',
      key: 'bed_type',
      width: 100,
    },
    {
      title: 'View',
      dataIndex: 'view_type',
      key: 'view_type',
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: RoomType) => (
        <Button
          type="primary"
          shape="circle"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRoomType(record);
            setDrawerVisible(true);
          }}
          size="small"
        />
      ),
    },
  ];

  const roomColumns = [
    {
      title: 'Room Number',
      dataIndex: 'room_number',
      key: 'room_number',
      width: 120,
      sorter: (a: Room, b: Room) => a.room_number.localeCompare(b.room_number),
    },
    {
      title: 'Room Type',
      dataIndex: 'room_type_id',
      key: 'room_type',
      width: 150,
      render: (id: string) => {
        const rt = roomTypes.find(t => t.id === id);
        return rt ? rt.name : 'Unknown';
      }
    },
    {
      title: 'Floor',
      dataIndex: 'floor',
      key: 'floor',
      width: 80,
      align: 'center' as const,
      sorter: (a: Room, b: Room) => a.floor - b.floor,
    },
    {
      title: 'AC Status',
      dataIndex: 'ac_status',
      key: 'ac_status',
      width: 100,
      render: (status: string) => {
        const value = status || 'both';
        let color = 'default';
        let label = 'Both';
        if (value === 'ac') { color = 'cyan'; label = 'AC'; }
        else if (value === 'non_ac') { color = 'orange'; label = 'Non-AC'; }
        
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Last Cleaned',
      dataIndex: 'last_cleaned',
      key: 'last_cleaned',
      width: 130,
      render: (date?: string) => date ?  dayjs(date).format('MMM D, YYYY') : 'N/A',
    },
    {
      title: 'Maintenance Notes',
      dataIndex: 'maintenance_notes',
      key: 'maintenance_notes',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: Room) => (
        <Button
          type="primary"
          shape="circle"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRoom(record);
            setRoomDrawerVisible(true);
          }}
          size="small"
        />
      ),
    },
  ];

  return (
    <div className="rooms-container">
      <div className="rooms-header">
        <h1>Room Management</h1>
        <p>Manage room types and individual rooms</p>
      </div>

      <Tabs
        activeKey={selectedTab}
        onChange={handleTabChange}
        items={[
          {
            key: '1',
            label: 'Room Types',
            children: (
              <div>
                <div className="rooms-actions">
                  {canManage && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRoomType}>
                      Add Room Type
                    </Button>
                  )}
                  <Button onClick={fetchRoomTypes} loading={loading}>
                    Refresh
                  </Button>
                </div>

                <Spin spinning={loading}>
                  <Table
                    columns={roomTypeColumns}
                    dataSource={roomTypes}
                    rowKey="id"
                    onRow={(record) => ({
                      onClick: () => {
                        setSelectedRoomType(record);
                        setDrawerVisible(true);
                      },
                      style: { cursor: 'pointer' }
                    })}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} room types`,
                    }}
                    scroll={{ x: 1200 }}
                  />
                </Spin>
              </div>
            ),
          },
          {
            key: '2',
            label: 'Rooms',
            children: (
              <div>
                <div className="rooms-actions">
                  {canManage && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRoom}>
                      Add Room
                    </Button>
                  )}
                  <Button onClick={fetchRooms} loading={loading}>
                    Refresh
                  </Button>
                </div>

                <Spin spinning={loading}>
                  <Table
                    columns={roomColumns}
                    dataSource={rooms}
                    rowKey="id"
                    onRow={(record) => ({
                      onClick: () => {
                        setSelectedRoom(record);
                        setRoomDrawerVisible(true);
                      },
                      style: { cursor: 'pointer' }
                    })}
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} rooms`,
                    }}
                    scroll={{ x: 1200 }}
                  />
                </Spin>
              </div>
            ),
          },
          {
            key: '3',
            label: 'Availability Grid',
            children: <ReservationCalendar />,
          },
          {
            key: '4',
            label: 'Offers & Promotions',
            children: <OffersManagement />,
          },
        ]}
      />

      {/* Room Type Modal */}
      <RoomTypeModal
        open={roomTypeModalVisible}
        onCancel={() => setRoomTypeModalVisible(false)}
        form={roomTypeForm}
        editingRoomType={editingRoomType}
        onSubmit={handleRoomTypeSubmit}
        loading={loading}
        canManage={canManage}
        handleRoomContentUpload={handleRoomContentUpload}
        handleRoomGalleryUpload={handleRoomGalleryUpload}
      />

      {/* Room Modal */}
      <RoomModal
        open={roomModalVisible}
        onCancel={() => setRoomModalVisible(false)}
        form={roomForm}
        editingRoom={editingRoom}
        onSubmit={handleRoomSubmit}
        loading={loading}
        roomTypes={roomTypes}
      />

      {/* Room Type Details Drawer */}
      {selectedRoomType && (
        <Modal
          title={`${selectedRoomType.name} Details`}
          open={drawerVisible}
          onCancel={() => setDrawerVisible(false)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 16 }}>
              <Button onClick={() => setDrawerVisible(false)}>Close</Button>
              {canManage && (
                <>
                  <Popconfirm
                    title="Delete this room type?"
                    description="This will remove room details and related images."
                    onConfirm={() => {
                      setDrawerVisible(false);
                      handleDeleteRoomType(selectedRoomType);
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    style={{ backgroundColor: '#B79A68', borderColor: '#B79A68' }}
                    icon={<EditOutlined />}
                    onClick={() => {
                      setDrawerVisible(false);
                      handleEditRoomType(selectedRoomType);
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
            </div>
          }
          width={760}
          styles={{ body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', padding: '16px' } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', color: '#000' }}>
            {/* Overview Section */}
            <div>
               <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                 <InfoCircleOutlined /> General Information
               </h3>
               <div>
                 <Descriptions column={2}>
                   <Descriptions.Item label="Name" span={2}>{selectedRoomType.name}</Descriptions.Item>
                   <Descriptions.Item label="Slug" span={2}>{selectedRoomType.slug || 'N/A'}</Descriptions.Item>
                   <Descriptions.Item label="Description" span={2}>{selectedRoomType.description}</Descriptions.Item>
                 </Descriptions>
               </div>
            </div>

            {/* Specifications & Pricing */}
            <Row gutter={16}>
              <Col span={12}>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AppstoreOutlined /> Specifications
                </h3>
                <div>
                  <Descriptions column={1}>
                    <Descriptions.Item label="Max Occupancy">{selectedRoomType.max_occupancy} guests</Descriptions.Item>
                    <Descriptions.Item label="Size">{selectedRoomType.size_sqm} sqm</Descriptions.Item>
                    <Descriptions.Item label="Bed Type">{selectedRoomType.bed_type}</Descriptions.Item>
                    <Descriptions.Item label="View">{selectedRoomType.view_type}</Descriptions.Item>
                    <Descriptions.Item label="AC Preference">
                      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', border: '1px solid #B79A68', color: '#000', fontSize: '12px', fontWeight: 500 }}>
                        {selectedRoomType.ac_status_preference === 'both' ? 'Both (AC/Non-AC)' : 
                         selectedRoomType.ac_status_preference === 'ac' ? 'AC Only' : 'Non-AC Only'}
                      </span>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
              
              <Col span={12}>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DollarOutlined /> Pricing
                </h3>
                <div>
                  <Descriptions column={1}>
                    <Descriptions.Item label="Base Price">
                      <strong>{formatLkr(selectedRoomType.base_price)}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Extra Bed">
                      {selectedRoomType.extra_bed_price !== undefined && selectedRoomType.extra_bed_price > 0 ? formatLkr(selectedRoomType.extra_bed_price) : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="AC Base Price">
                      {selectedRoomType.ac_base_price ? formatLkr(selectedRoomType.ac_base_price) : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Non-AC Base Price">
                      {selectedRoomType.non_ac_base_price ? formatLkr(selectedRoomType.non_ac_base_price) : 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
            </Row>

            {/* Features & Amenities */}
            <div>
               <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                 <StarOutlined /> Features & Amenities
               </h3>
               <div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 4, fontWeight: 500 }}>Amenities:</div>
                    <div>
                      {selectedRoomType.amenities?.length ? selectedRoomType.amenities.join(', ') : 'No amenities listed'}
                    </div>
                  </div>
                  <div>
                    <div style={{ marginBottom: 4, fontWeight: 500 }}>Highlights:</div>
                    <div>
                      {selectedRoomType.highlight_points?.length ? selectedRoomType.highlight_points.join(', ') : 'No highlights listed'}
                    </div>
                  </div>
               </div>
            </div>

            {/* Gallery */}
            {selectedRoomType.images && selectedRoomType.images.length > 0 && (
              <div>
                 <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                   <PictureOutlined /> Gallery
                 </h3>
                 <div>
                   <Image.PreviewGroup>
                     <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                       {selectedRoomType.images.map((img, idx) => (
                         <Image 
                           key={idx} 
                           src={img.image_url} 
                           alt={img.alt_text || `Room Image ${idx+1}`} 
                           width={80} 
                           height={80} 
                           style={{ objectFit: 'cover', borderRadius: 4 }} 
                         />
                       ))}
                     </div>
                   </Image.PreviewGroup>
                 </div>
              </div>
            )}

            {/* Website Content */}
            <div>
               <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                 <GlobalOutlined /> Website Content Display
               </h3>
               <Descriptions column={1}>
                 <Descriptions.Item label="Hero Title">{selectedRoomType.hero_title || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Hero Subtitle">{selectedRoomType.hero_subtitle || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Hero Image">
                   {selectedRoomType.hero_image_url ? (
                     <Image src={selectedRoomType.hero_image_url} alt="Hero" width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                   ) : 'N/A'}
                 </Descriptions.Item>
                 <Descriptions.Item label="Overview Kicker">{selectedRoomType.overview_kicker || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Overview Title">{selectedRoomType.overview_title || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Overview Description">{selectedRoomType.overview_description || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Overview Image">
                   {selectedRoomType.overview_image_url ? (
                     <Image src={selectedRoomType.overview_image_url} alt="Overview" width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                   ) : 'N/A'}
                 </Descriptions.Item>
                 <Descriptions.Item label="Detail Kicker">{selectedRoomType.detail_kicker || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Detail Title">{selectedRoomType.detail_title || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Detail Description">{selectedRoomType.detail_description || 'N/A'}</Descriptions.Item>
                 <Descriptions.Item label="Detail Image">
                   {selectedRoomType.detail_image_url ? (
                     <Image src={selectedRoomType.detail_image_url} alt="Detail" width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                   ) : 'N/A'}
                 </Descriptions.Item>
                 <Descriptions.Item label="Booking Panel">{selectedRoomType.booking_panel_title || 'N/A'}</Descriptions.Item>
               </Descriptions>
            </div>
          </div>
        </Modal>
      )}

      {/* Room Details Modal */}
      {selectedRoom && (
        <Modal
          title={`Room ${selectedRoom.room_number} Details`}
          open={roomDrawerVisible}
          onCancel={() => setRoomDrawerVisible(false)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: 16 }}>
              <Button onClick={() => setRoomDrawerVisible(false)}>Close</Button>
              {canManage && (
                <>
                  <Popconfirm
                    title="Delete this room?"
                    description="This action cannot be undone."
                    onConfirm={() => {
                      setRoomDrawerVisible(false);
                      handleDeleteRoom(selectedRoom);
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    style={{ backgroundColor: '#B79A68', borderColor: '#B79A68' }}
                    icon={<EditOutlined />}
                    onClick={() => {
                      setRoomDrawerVisible(false);
                      handleEditRoom(selectedRoom);
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
            </div>
          }
          width={760}
          styles={{ body: { overflowX: 'hidden', padding: '16px' } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', color: '#000' }}>
            <Row gutter={16}>
              <Col span={12}>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <KeyOutlined /> Room Info
                </h3>
                <div>
                  <Descriptions column={1}>
                    <Descriptions.Item label="Room Number"><strong>{selectedRoom.room_number}</strong></Descriptions.Item>
                    <Descriptions.Item label="Room Type">
                      {roomTypes.find(t => t.id === selectedRoom.room_type_id)?.name || 'Unknown'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Floor">{selectedRoom.floor}</Descriptions.Item>
                    <Descriptions.Item label="AC Status">
                      {(() => {
                        const value = selectedRoom.ac_status || 'both';
                        const text = value === 'ac' ? 'AC Only' : value === 'non_ac' ? 'Non-AC Only' : 'Both (AC/Non-AC)';
                        return (
                          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', border: '1px solid #B79A68', color: '#000', fontSize: '12px', fontWeight: 500 }}>
                            {text}
                          </span>
                        );
                      })()}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
              <Col span={12}>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ToolOutlined /> Status
                </h3>
                <div>
                  <Descriptions column={1}>
                    <Descriptions.Item label="Status">
                      <span style={{ textTransform: 'capitalize' }}>{selectedRoom.status}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Cleaned">
                      {selectedRoom.last_cleaned ? dayjs(selectedRoom.last_cleaned).format('MMM D, YYYY h:mm A') : 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
            </Row>
            {selectedRoom.maintenance_notes && (
              <div>
                <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <InfoCircleOutlined /> Maintenance Notes
                </h3>
                <div>
                  {selectedRoom.maintenance_notes}
                </div>
              </div>
            )}
            {!selectedRoom.maintenance_notes && (
               <div>
                  <h3 style={{ fontSize: 16, color: '#B79A68', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <InfoCircleOutlined /> Maintenance Notes
                  </h3>
                  <div>
                    No maintenance notes available.
                  </div>
               </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Rooms;
